import * as cocoSsd from '@tensorflow-models/coco-ssd';
import '@tensorflow/tfjs-backend-webgl';
import * as tf from '@tensorflow/tfjs-core';
import type { DetectionBox, DetectionClass, DetectionResult, Detector } from './Detector';

export interface CocoSsdOptions {
  base?: 'lite_mobilenet_v2' | 'mobilenet_v2' | 'mobilenet_v1';
  minScore?: number;
  maxDetections?: number;
}

const CLASS_MAP: Record<string, DetectionClass> = {
  person: 'person',
  'sports ball': 'ball',
};

export class CocoSsdDetector implements Detector {
  readonly id = 'coco-ssd';
  readonly label: string;
  private model: cocoSsd.ObjectDetection | null = null;

  constructor(private readonly opts: CocoSsdOptions = {}) {
    this.label = `COCO-SSD (${opts.base ?? 'lite_mobilenet_v2'})`;
  }

  async initialize(): Promise<void> {
    await tf.setBackend('webgl');
    await tf.ready();
    this.model = await cocoSsd.load({ base: this.opts.base ?? 'lite_mobilenet_v2' });
  }

  async detect(source: HTMLVideoElement | HTMLCanvasElement): Promise<DetectionResult> {
    if (!this.model) throw new Error('CocoSsdDetector not initialized');
    const w = ('videoWidth' in source ? source.videoWidth : source.width) || 1;
    const h = ('videoHeight' in source ? source.videoHeight : source.height) || 1;
    const minScore = this.opts.minScore ?? 0.4;
    const max = this.opts.maxDetections ?? 30;

    const t0 = performance.now();
    const raw = await this.model.detect(source, max, minScore);
    const inferenceTimeMs = performance.now() - t0;

    const detections: DetectionBox[] = [];
    for (const p of raw) {
      const mapped = CLASS_MAP[p.class];
      if (!mapped) continue;
      const [x, y, bw, bh] = p.bbox;
      detections.push({
        class: mapped,
        x: x / w,
        y: y / h,
        w: bw / w,
        h: bh / h,
        score: p.score,
      });
    }

    return { detections, inferenceTimeMs };
  }

  dispose(): void {
    this.model?.dispose();
    this.model = null;
  }
}
